import {
  Box,
  Checkbox,
  CollapsibleCard,
  DatePicker,
  Grid,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { InformationIcon } from '@atgfr8/icons';
import { SelectInput, SelectInputCustom } from '@components';
import { HoverPop } from '@components/HoverPop';
import { userRoles } from '@constants/common';
import { FeatureFlags } from '@constants/featureFlags';
import { fetchCollectionsTier } from '@store/collectionsDashboard/actions';
import { collectionDetailsStore } from '@store/collectionsDashboard/store';
import {
  customerDetailsStore,
  updateCustomerDetailsState,
} from '@store/customers/customerDetails';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { userStore } from '@store/user';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { isSuperAdmin } from '@utils/utils';
import { intersection } from 'lodash';
import { createMemo, createSignal, onMount, Show } from 'solid-js';

import {
  BillingTermList,
  CollectionTermList,
  InvoiceMethodList,
} from '../../constants';
import { invoicingClasses as classes } from './classes';

const Invoicing = () => {
  const [searchValue, setSearchValue] = createSignal('');
  const [inputRef, setInputRef] = createSignal<HTMLInputElement | undefined>(
    undefined,
  );

  const isReadonly = () => {
    return (
      intersection(userStore.user.roles, [
        userRoles.ADMIN,
        userRoles.ADMIN_LITE,
      ]).length <= 0
    );
  };

  const tmsSearch = createMemo(() => {
    return {
      value: '__search__',
      disabled: true,
      class: 'p-0! pointer-events-auto! cursor-default!',
      label: (
        <div class="p-2 [&>*]:w-full">
          <TextField
            label="Search..."
            value={searchValue()}
            onChange={(val) => {
              setSearchValue(val);
              inputRef()?.focus();
            }}
            ref={setInputRef}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
            on:blur={(event) => {
              setTimeout(() => {
                inputRef()?.focus();
              }, 0);
              event.stopPropagation();
            }}
            color="primary"
            fullWidth
          />
        </div>
      ),
    };
  });

  const tmsPortalOptions = createMemo(() => {
    return customerDetailsStore.portals
      .filter((portal) => {
        return (portal.displayName as string)
          .toLowerCase()
          .includes(searchValue().toLowerCase());
      })
      .map((portal) => ({
        label: portal.displayName,
        value: portal.id.toString(),
      }));
  });

  const activeCollectionTiers = createMemo(() => {
    const currentTime = new Date().getTime();
    const assignedTierId = customerDetailsStore.customer.collectionTierId;
    return collectionDetailsStore.collectionTier.filter(
      (tier) =>
        tier.expirationDate == null ||
        new Date(tier.expirationDate).getTime() >= currentTime ||
        String(tier.id) === String(assignedTierId),
    );
  });

  onMount(() => {
    void fetchCollectionsTier();
  });

  return (
    <Box class={classes.invoiceContainer}>
      <CollapsibleCard startTitle="Invoicing" class="mb-2">
        <Grid container rowSpacing={4} columnSpacing={2} spacing={1}>
          <Grid item size={{ sm: 12, md: 6, lg: 8 }}>
            <TextField
              multiline
              label="Billing Instructions"
              rows={6}
              placeholder="Enter your billing instructions here."
              value={customerDetailsStore.customer.billingInstructions || ''}
              onChange={(val) =>
                updateCustomerDetailsState({ billingInstructions: val })
              }
              disabled={isReadonly()}
              maxLength={1000}
              dataTestId="invoicing-billing-instructions-textAreaField"
            />
          </Grid>
          <Grid item size={{ sm: 12, md: 6, lg: 4 }}>
            <Grid container direction={'column'} spacing={4}>
              <Grid item>
                <SelectInput
                  label="Invoice Method"
                  menuItems={InvoiceMethodList.map((i) => ({
                    label: i.label,
                    value: String(i.value),
                  }))}
                  value={customerDetailsStore.customer.invoiceMethod ?? ''}
                  onChange={(value) =>
                    updateCustomerDetailsState({
                      invoiceMethod: value,
                    })
                  }
                  disabled={isReadonly()}
                  dataTestId={formatDataTestId('Invoice Method', 'selectInput')}
                />
              </Grid>
              <Show
                when={
                  (customerDetailsStore.customer.invoiceMethod ===
                    'Third Party' ||
                    customerDetailsStore.customer.invoiceMethod === 'EDI') &&
                  isFeatureFlagEnabled(FeatureFlags.FeatureCustomerTmsPortals)()
                }
              >
                <SelectInputCustom
                  label="TMS Portal"
                  class="text-[14px] bg-white min-w-[200px]"
                  disabled={isReadonly()}
                  value={
                    customerDetailsStore.customer.tmsPortalViewModel?.id.toString() ||
                    ''
                  }
                  onBlur={() => setSearchValue('')}
                  menuItems={[tmsSearch(), ...tmsPortalOptions()]}
                  onChange={(value) => {
                    updateCustomerDetailsState({
                      tmsPortalViewModel: customerDetailsStore.portals.find(
                        (portal) => portal.id === parseInt(value),
                      ),
                    });

                    setSearchValue('');
                    inputRef()?.focus();
                  }}
                  dataTestId={formatDataTestId('TMS Portal', 'selectInput')}
                  menuClass="min-w-[320px]"
                  name="TMS Portal"
                />
              </Show>
              <Grid item>
                <SelectInput
                  label="Billing Terms"
                  menuItems={BillingTermList.map((i) => ({
                    label: i.label,
                    value: String(i.value),
                  }))}
                  value={customerDetailsStore.customer.paymentTerms || ''}
                  onChange={(value) =>
                    updateCustomerDetailsState({
                      paymentTerms: value,
                    })
                  }
                  disabled={isReadonly()}
                  dataTestId={formatDataTestId('Billing Terms', 'selectInput')}
                />
              </Grid>
              <Grid item>
                <SelectInput
                  label="Collection Terms"
                  menuItems={CollectionTermList.map((i) => ({
                    label: i.label,
                    value: String(i.value),
                  }))}
                  value={customerDetailsStore.customer.collectionTerms || ''}
                  onChange={(value) =>
                    updateCustomerDetailsState({
                      collectionTerms: value,
                    })
                  }
                  disabled={isReadonly()}
                  dataTestId={formatDataTestId(
                    'Collection Terms',
                    'selectInput',
                  )}
                />
              </Grid>
              <Grid item>
                <SelectInput
                  label="Collection Tier"
                  placeholder="Select Collection Tier"
                  menuItems={activeCollectionTiers().map((tier) => ({
                    label: tier.tier,
                    value: String(tier.id),
                  }))}
                  value={
                    customerDetailsStore.customer.collectionTierId != null
                      ? String(customerDetailsStore.customer.collectionTierId)
                      : ''
                  }
                  onChange={(value) =>
                    updateCustomerDetailsState({
                      collectionTierId: value,
                    })
                  }
                  disabled={isReadonly()}
                  dataTestId={formatDataTestId(
                    'Collection Tier',
                    'selectInput',
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item size={{ sm: 12, md: 6, lg: 8 }}>
            <Grid container spacing={4}>
              <Grid item size={{ xs: 12, md: 6, lg: 6 }}>
                <Checkbox
                  size="xs"
                  color="info"
                  class="ml-[-9px] whitespace-normal break-words"
                  label="Hold Retractions"
                  checked={customerDetailsStore.customer.holdRetraction}
                  onChange={(value) => {
                    if (!value) {
                      updateCustomerDetailsState({
                        revisitRetractionDate: null,
                        holdRetraction: value,
                        exceptionReason: null,
                      });
                    } else {
                      updateCustomerDetailsState({ holdRetraction: value });
                    }
                  }}
                  disabled={isReadonly()}
                  dataTestId="invoicing-hold-retractions-checkboxInput"
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6, lg: 6 }}>
                <DatePicker
                  value={
                    customerDetailsStore.customer.revisitRetractionDate || ''
                  }
                  handleChange={(date) =>
                    updateCustomerDetailsState({
                      revisitRetractionDate: date,
                    })
                  }
                  class={classes.textFields}
                  disabled={isReadonly()}
                  label="Revisit Retraction Hold Date"
                  dataTestId="invoicing-revisit-retraction-date-datePicker"
                />
              </Grid>
              <Grid item>
                <TextField
                  multiline
                  label="Exception Reason"
                  rows={5}
                  placeholder="Enter your reason here."
                  value={customerDetailsStore.customer.exceptionReason || ''}
                  onChange={(val) =>
                    updateCustomerDetailsState({ exceptionReason: val })
                  }
                  disabled={isReadonly()}
                  maxLength={500}
                  dataTestId="invoicing-exception-reason-textAreaField"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item size={{ sm: 12, md: 6, lg: 4 }}>
            <Box class={classes.checkboxContainer}>
              <Box class={classes.checkboxInnerContainer}>
                <Checkbox
                  size="xs"
                  color="info"
                  class="ml-[-9px] whitespace-normal break-words"
                  label={'Billing Info Verified'}
                  checked={customerDetailsStore.customer.verified}
                  onChange={(value) =>
                    updateCustomerDetailsState({ verified: value })
                  }
                  disabled={isReadonly()}
                  dataTestId="invoicing-billing-info-verified-checkboxInput"
                />
              </Box>
              <Box class={classes.checkboxInnerContainer}>
                <Checkbox
                  size="xs"
                  color="info"
                  class="ml-[-9px] whitespace-normal break-words"
                  label={'Paperwork Required To Invoice'}
                  checked={
                    customerDetailsStore.customer.invoicePaperworkRequired
                  }
                  onChange={(value) =>
                    updateCustomerDetailsState({
                      invoicePaperworkRequired: value,
                    })
                  }
                  disabled={isReadonly()}
                  dataTestId="invoicing-paperwork-required-to-invoice-checkboxInput"
                />
              </Box>
              <Box class={classes.checkboxInnerContainer}>
                <Checkbox
                  size="xs"
                  color="info"
                  class="ml-[-9px] whitespace-normal break-words"
                  label={'Needs Pre-approval'}
                  checked={Boolean(
                    customerDetailsStore.customer.needsPreapproval,
                  )}
                  onChange={(value) =>
                    updateCustomerDetailsState({ needsPreapproval: value })
                  }
                  disabled={!isSuperAdmin()}
                  dataTestId="invoicing-needs-preapproval-checkboxInput"
                />
              </Box>
              <Box class={classes.checkboxInnerContainer}>
                <Checkbox
                  size="xs"
                  color="info"
                  class="ml-[-9px] whitespace-normal break-words"
                  label={'Charge Credit Card Payment Fee'}
                  checked={Boolean(
                    customerDetailsStore.customer.chargeCreditCardFee,
                  )}
                  onChange={(value) =>
                    updateCustomerDetailsState({
                      chargeCreditCardFee: value,
                    })
                  }
                  dataTestId="invoicing-charge-credit-card-payment-fee-checkboxInput"
                />
              </Box>
              <Box class={classes.checkboxInnerContainer}>
                <Checkbox
                  size="xs"
                  color="info"
                  class="ml-[-9px] whitespace-normal break-words"
                  label={'Require Empty Carrier Status Before Processing Load'}
                  checked={
                    customerDetailsStore.customer
                      .requireEmptyBeforeProcessingLoad ?? false
                  }
                  disabled={
                    customerDetailsStore.customer
                      .requireEmptyBeforeProcessingLoad == null ||
                    customerDetailsStore.customer
                      .officeRequireEmptyBeforeProcessingLoad === true
                  }
                  onChange={(value) =>
                    updateCustomerDetailsState({
                      requireEmptyBeforeProcessingLoad: value,
                    })
                  }
                  dataTestId="invoicing-require-empty-carrier-status-checkboxInput"
                />
                <Show
                  when={
                    customerDetailsStore.customer
                      .officeRequireEmptyBeforeProcessingLoad === true
                  }
                >
                  <div class="absolute top-0 bottom-0 right-0 flex items-center justify-center pr-2">
                    <HoverPop
                      anchor={<InformationIcon color="rgb(70, 141, 181)" />}
                      render
                    >
                      <div class="p-2 max-w-[350px]">
                        Office level override is enabled. Disable this setting
                        to manage empty carrier status requirements at the
                        customer level.
                      </div>
                    </HoverPop>
                  </div>
                </Show>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CollapsibleCard>
    </Box>
  );
};

export default Invoicing;
